import { useMemo } from 'react';

import type { CommunityPost } from '~/interfaces/community';

import { isAuthorProtectedFromBan, isAuthorProtectedFromUnban } from '../_utils/permissions';
import { useCommunityMembership } from './useCommunityMembership';

export interface CommunityPostPermissions {
  isAuthor: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canPin: boolean;
  canMove: boolean;
  canPinToProfile: boolean;
  canReport: boolean;
  canFollowAuthor: boolean;
  canGoToPost: boolean;
  canShare: boolean;
  canHideComments: boolean;
  canCloseComments: boolean;
  canBanAuthor: boolean;
  canUnbanAuthor: boolean;
}

export const usePostPermissions = (post: CommunityPost): CommunityPostPermissions => {
  const { currentMember, permissions } = useCommunityMembership();

  return useMemo(() => {
    const isAuthor = currentMember?.id === post.author.id;
    const isAuthorBanned = post.author.computed?.banned === true;
    const {
      isRestricted,
      isMember,
      canInteract,
      canPinAnyPost,
      canMoveAnyPost,
      canRemoveAnyPost,
      canHideAnyComments,
      canCloseAnyComments,
      canBanMember,
      isManager,
    } = permissions;
    const authorProtectedFromBan = isAuthorProtectedFromBan(isManager, post.author.roles);
    const authorProtectedFromUnban = isAuthorProtectedFromUnban(isManager, post.author.roles);

    return {
      isAuthor,
      canEdit: isAuthor && canInteract,
      canDelete: (isAuthor || canRemoveAnyPost) && canInteract,
      canPin: canPinAnyPost,
      canMove: canMoveAnyPost,
      canPinToProfile: isAuthor && canInteract,
      canReport: isMember && !isAuthor && canInteract,
      canFollowAuthor: isMember && !isAuthor && !isRestricted,
      canGoToPost: true,
      canShare: !isRestricted,
      canHideComments: canHideAnyComments,
      canCloseComments: canCloseAnyComments,
      canBanAuthor: canBanMember && !isAuthor && !isAuthorBanned && !authorProtectedFromBan,
      canUnbanAuthor: canBanMember && !isAuthor && isAuthorBanned && !authorProtectedFromUnban,
    };
  }, [currentMember, permissions, post.author]);
};
